import { IDriverset, TDriversetParameter } from '../api/models';

type IGoParams =
  { [K in TDriversetParameter]?: number } & {
    julianDay?: number;
  };
  
/**
   * Given a driverset, compute a complete set of 366 interpolated AquaFishGoParams.
   * Each element corresponds to a Julian day (0–365).
   */
const interpolateDriverSet = (driverset: IDriverset): IGoParams[] => {
    const YEARSTODAYS = 365;
    const NODATA = NaN;

    const parameters: TDriversetParameter[] = [
      "waterTemperature",
      "salinity",
      "chlorophyll",
      "currentSpeed",
      "dissolvedOxygen",
      "pom",
      "tpm"
    ];

    /** Build a 365-element array (indexes 0–364) with NODATA where missing */
    function buildArray(param: TDriversetParameter): number[] {
      const arr = new Array<number>(YEARSTODAYS).fill(NODATA);
      for (const d of driverset.dates) {
        if (d.day === null) continue;
        const idx = d.day - 1; // C# stores day 1 at index 0
        if (idx >= 0 && idx < YEARSTODAYS) {
          const v = d.values[param];
          if (typeof v === "number" && !isNaN(v)) arr[idx] = v;
        }
      }
      return arr;
    }

    /** Return true if value is not NODATA */
    function isData(v: number): boolean {
      return typeof v === "number" && !isNaN(v);
    }

    function getDataPoints(arr: number[]): number {
      let count = 0;
      for (let i = 0; i < YEARSTODAYS; i++) {
        if (isData(arr[i])) count++;
      }
      return count;
    }

    function findNextDay(start: number, arr: number[]): number | null {
      for (let i = start; i < YEARSTODAYS; i++) {
        if (isData(arr[i])) return i;
      }
      return null;
    }

    /** Perform C# style interpolation directly mutating arr */
    function interpolateDrivers(arr: number[]): void {
      const dataPoints = getDataPoints(arr);
      if (dataPoints === 0) return;

      const firstDP = findNextDay(0, arr);
      if (firstDP === null) return;

      let FirstDP = firstDP;   // moving
      const MyDP = firstDP;    // original first

      // ---- SINGLE DATA POINT ----
      if (findNextDay(FirstDP + 1, arr) === null) {
        for (let i = 0; i < YEARSTODAYS; i++) {
          if (i !== FirstDP) arr[i] = arr[FirstDP];
        }
        return;
      }

      // ---- MULTIPLE DATA POINTS ----
      for (let i = 1; i < dataPoints; i++) {
        const NextDP = findNextDay(FirstDP + 1, arr);
        if (NextDP === null) break;

        const Distance = NextDP - FirstDP;
        const X = arr[FirstDP];
        const Y = arr[NextDP];

        for (let j = FirstDP + 1; j < NextDP; j++) {
          arr[j] = X + (Y - X) * (j - FirstDP) / Distance;
        }

        FirstDP = NextDP;
      }

      // ---- WRAP-AROUND INTERPOLATION ----
      const NextDP = FirstDP;
      FirstDP = MyDP;

      const Distance = FirstDP + YEARSTODAYS - NextDP;
      const X = arr[NextDP];
      const Y = arr[FirstDP];

      for (let i = NextDP + 1; i < YEARSTODAYS; i++) {
        arr[i] = X + (Y - X) * (i - NextDP) / Distance;
      }
      for (let i = 0; i < FirstDP; i++) {
        arr[i] = X + (Y - X) * (i + YEARSTODAYS - NextDP) / Distance;
      }
    }

    // ---- CREATE PARAMETER ARRAYS AND INTERPOLATE ----
    const paramArrays: Record<string, number[]> = {};

    for (const p of parameters) {
      const arr = buildArray(p);
      interpolateDrivers(arr);
      paramArrays[p] = arr;
    }

    // ---- BUILD OUTPUT: 366 DAYS (0..365) ----
    const results: IGoParams[] = [];

    for (let day = 0; day <= YEARSTODAYS; day++) {
      // Map day 0 -> idx 0, day 1 -> idx 1, ..., day 365 -> idx 0 (wrap)
      const idx = day % YEARSTODAYS;

      const row: IGoParams = {
        julianDay: day,
        waterTemperature: paramArrays.waterTemperature[idx],
        salinity: paramArrays.salinity[idx],
        chlorophyll: paramArrays.chlorophyll[idx],
        currentSpeed: paramArrays.currentSpeed[idx],
        dissolvedOxygen: paramArrays.dissolvedOxygen[idx],
        pom: paramArrays.pom[idx],
        tpm: paramArrays.tpm[idx],
      };

      results.push(row);
    }

    return results;
  }

  export { interpolateDriverSet }
